/**
 * `/settings/teams/:teamUuid` — a team's member roster.
 *
 * Lists who's in the team (resolved to real names/emails via the
 * enriched members endpoint), and lets admins add existing org members
 * or remove them. Invites happen at the org level (Settings → Members);
 * here you only move existing people in and out of the team.
 *
 * Renders into the settings layout's <Island>. Admin-only writes —
 * affordances are hidden for non-admins (the API also 403s).
 */
import { ConfirmDialog } from '@/components/primitives/ConfirmDialog';
import { EmptyState } from '@/components/primitives/EmptyState';
import { PageActions } from '@/components/primitives/PageActions';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field, FieldLabel } from '@/components/ui/field';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import type { OrgRole, TeamMember } from '@/lib/api-types';
import { toastError, toastFromError, toastSuccess } from '@/lib/errors';
import {
  useActiveOrg,
  useAddTeamMember,
  useMembers,
  useRemoveTeamMember,
  useTeamMembers,
  useWorkspace,
} from '@/lib/queries';
import { useOrganization } from '@clerk/react';
import { Trash, UserPlus, Users } from '@phosphor-icons/react';
import { createFileRoute } from '@tanstack/react-router';
import { type ReactElement, useMemo, useState } from 'react';

/**
 * Role select labels. Mirrors the option text inside the SelectContent —
 * needed because Base UI's `Select.Value` renders the raw `value`
 * string (e.g. "member") until the popup mounts and the matching
 * `SelectItem`'s ItemText registers. That caused a momentary lowercase
 * flash on first paint; rendering through this map via `SelectValue`'s
 * render-prop child sidesteps the popup-mount dance entirely.
 */
const ROLE_LABELS: Record<OrgRole, string> = {
  admin: 'Admin',
  member: 'Member',
};

export const Route = createFileRoute('/_app/settings/teams/$teamUuid')({
  component: TeamMembersPage,
});

function memberLabel(m: { full_name?: string | null; email?: string; user_uuid: string }): string {
  return m.full_name?.trim() || m.email || m.user_uuid;
}

function TeamMembersPage(): ReactElement {
  const { teamUuid } = Route.useParams();
  const workspace = useWorkspace();
  const { orgId } = useActiveOrg();
  const activeOrg = workspace.data?.organisations.find((o) => o.auth_provider_id === orgId) ?? null;
  const isAdmin = activeOrg?.role === 'admin';
  const orgUuid = activeOrg?.uuid ?? '';
  const team = activeOrg?.teams.find((t) => t.uuid === teamUuid) ?? null;

  const members = useTeamMembers(teamUuid);
  const remove = useRemoveTeamMember();
  const [addOpen, setAddOpen] = useState(false);
  const [toRemove, setToRemove] = useState<TeamMember | null>(null);

  const rows = members.data ?? [];

  // Clerk holds the avatar URLs (PII dropped from Supabase in §9.25,
  // see migration 20260505000000_drop_identity_pii.sql). Pull the org's
  // full Clerk roster and key by lowercased email so each team-member
  // row from the API can look up an image without an extra round-trip.
  // The Clerk hook is already cached at the app shell, so this is just
  // an in-memory read for the second+ load.
  const { memberships: clerkMemberships } = useOrganization({
    memberships: { infinite: false, pageSize: 100 },
  });
  const avatarByEmail = useMemo(() => {
    const map = new Map<string, string>();
    for (const cm of clerkMemberships?.data ?? []) {
      const email = cm.publicUserData?.identifier?.toLowerCase();
      const url = cm.publicUserData?.imageUrl;
      if (email && url) map.set(email, url);
    }
    return map;
  }, [clerkMemberships?.data]);

  async function onRemoveConfirm(): Promise<void> {
    if (!toRemove) return;
    try {
      await remove.mutateAsync({ teamUuid, user_uuid: toRemove.user_uuid });
      toastSuccess('Member removed', memberLabel(toRemove));
      setToRemove(null);
    } catch (err) {
      toastError('Failed to remove member', (err as Error)?.message);
    }
  }

  return (
    <div className="space-y-6">
      {/* No "All teams" back-link: the settings shell's breadcrumb
          already ends Settings → Team → Teams → this uuid, so the climb
          back was a second control pointing at a crumb one line above it. */}
      <header className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-[var(--color-ink)]">
            {team?.name ?? 'Team'} members
          </h2>
          <p className="text-sm text-[var(--color-mute)]">
            People in this team.{' '}
            {isAdmin ? 'Add existing workspace members or remove them.' : 'Only admins can edit.'}
          </p>
        </div>
        {isAdmin && (
          <PageActions>
            <Button onClick={() => setAddOpen(true)} disabled={!orgUuid}>
              <UserPlus aria-hidden="true" />
              Add member
            </Button>
          </PageActions>
        )}
      </header>

      {members.isLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={Users}
          title="No members yet"
          description="Add existing workspace members to this team."
          action={
            isAdmin ? (
              <Button onClick={() => setAddOpen(true)} disabled={!orgUuid}>
                <UserPlus aria-hidden="true" />
                Add member
              </Button>
            ) : undefined
          }
        />
      ) : (
        <section className="overflow-hidden rounded-2xl border border-[var(--color-line)] bg-[var(--color-paper)]">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-[var(--color-line-soft)] border-b text-left text-[var(--color-mute)] text-xs uppercase tracking-[0.06em]">
                  <th className="px-4 py-2 font-medium">Member</th>
                  <th className="px-4 py-2 font-medium">Role</th>
                  <th className="w-px px-4 py-2" />
                </tr>
              </thead>
              <tbody>
                {rows.map((m) => {
                  const label = memberLabel(m);
                  const avatarSrc = m.email ? avatarByEmail.get(m.email.toLowerCase()) : undefined;
                  const initial = (m.full_name || m.email || m.user_uuid)
                    .trim()
                    .slice(0, 1)
                    .toUpperCase();
                  return (
                    <tr key={m.user_uuid} className="border-[var(--color-line-soft)]/70 border-b">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <Avatar className="size-9 shrink-0 bg-[var(--color-paper-alt)]">
                            {avatarSrc && <AvatarImage src={avatarSrc} alt="" />}
                            <AvatarFallback className="text-sm font-medium text-[var(--color-ink-soft)]">
                              {initial}
                            </AvatarFallback>
                          </Avatar>
                          <div className="min-w-0">
                            <div className="truncate font-medium text-[var(--color-ink)]">
                              {label}
                            </div>
                            {m.full_name && m.email && (
                              <div className="truncate text-[var(--color-mute)] text-xs">
                                {m.email}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-[var(--color-ink-soft)]">
                        {ROLE_LABELS[m.role] ?? m.role}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {isAdmin && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setToRemove(m)}
                            aria-label={`Remove ${label}`}
                          >
                            <Trash aria-hidden="true" />
                            Remove
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <AddTeamMemberDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        teamUuid={teamUuid}
        orgUuid={orgUuid}
        existing={rows}
      />
      <ConfirmDialog
        open={toRemove !== null}
        onOpenChange={(next) => {
          if (!next) setToRemove(null);
        }}
        title="Remove from team?"
        description={
          <>
            <strong>{toRemove ? memberLabel(toRemove) : 'This member'}</strong> will be removed from
            this team. They keep their workspace access — this only changes team membership.
          </>
        }
        confirmLabel="Remove"
        destructive
        loading={remove.isPending}
        onConfirm={onRemoveConfirm}
      />
    </div>
  );
}

function AddTeamMemberDialog({
  open,
  onOpenChange,
  teamUuid,
  orgUuid,
  existing,
}: {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  teamUuid: string;
  orgUuid: string;
  existing: TeamMember[];
}): ReactElement {
  const orgMembers = useMembers(orgUuid);
  const add = useAddTeamMember();
  const [userUuid, setUserUuid] = useState<string | undefined>(undefined);
  const [role, setRole] = useState<OrgRole>('member');

  // Org members not already on the team.
  const candidates = useMemo(() => {
    const inTeam = new Set(existing.map((m) => m.user_uuid));
    return (orgMembers.data ?? []).filter((m) => !inTeam.has(m.user_uuid));
  }, [orgMembers.data, existing]);

  async function onAdd(): Promise<void> {
    if (!userUuid) return;
    try {
      await add.mutateAsync({ teamUuid, user_uuid: userUuid, role });
      toastSuccess('Member added');
      onOpenChange(false);
      setUserUuid(undefined);
      setRole('member');
    } catch (err) {
      toastFromError(err);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
        if (!next) {
          setUserUuid(undefined);
          setRole('member');
        }
      }}
    >
      <DialogPopup className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add member</DialogTitle>
        </DialogHeader>
        <DialogPanel>
          <div className="space-y-4">
            <Field>
              <FieldLabel htmlFor="add-team-member-user">Workspace member</FieldLabel>
              {orgMembers.isLoading ? (
                <Skeleton className="h-9 w-full" />
              ) : candidates.length === 0 ? (
                <p className="text-sm text-[var(--color-mute)]">
                  Everyone in the workspace is already on this team. Invite more people from
                  Settings → Members.
                </p>
              ) : (
                <Select
                  value={userUuid}
                  onValueChange={(v) => {
                    if (typeof v === 'string') setUserUuid(v);
                  }}
                >
                  <SelectTrigger id="add-team-member-user">
                    <SelectValue placeholder="Select a member" />
                  </SelectTrigger>
                  <SelectContent>
                    {candidates.map((m) => (
                      <SelectItem key={m.user_uuid} value={m.user_uuid}>
                        {memberLabel(m)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </Field>
            <Field>
              <FieldLabel htmlFor="add-team-member-role">Role</FieldLabel>
              <Select
                value={role}
                onValueChange={(v) => {
                  if (v === 'admin' || v === 'member') setRole(v);
                }}
              >
                <SelectTrigger id="add-team-member-role">
                  {/* Render the label via the map so the trigger never
                      flashes the raw lowercase value on first paint. */}
                  <SelectValue>{(v) => ROLE_LABELS[v as OrgRole] ?? 'Member'}</SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="member">Member</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>
        </DialogPanel>
        <DialogFooter>
          <DialogClose render={<Button variant="outline">Cancel</Button>} />
          <Button onClick={() => void onAdd()} loading={add.isPending} disabled={!userUuid}>
            Add to team
          </Button>
        </DialogFooter>
      </DialogPopup>
    </Dialog>
  );
}
